<template>
  <div class="timetable-widget grid">
    <h1 class="title t-h3" v-html="nbsp(widget.title)" />
    <div
      v-if="widget.description"
      class="description"
      v-html="nbsp(widget.description)"
    />
    <div class="wrapper">
      <TimetableFilters
        v-if="!props.widget.filter_type"
        :filters="filters"
        @reset-filters-click="resetFilters"
        @filter-change="setFilter"
      />
      <TimetableSwitcher v-if="isFirst" v-model="display" />
      <Timetable
        v-if="timetableData"
        :list="timetableData.results"
        :display="display"
      />
      <TimetablePagination
        :hide-load-more="!hasNextPage"
        :hide-link="!!isFirst && !query?.course"
        @load-more-click="changePage"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  PublicCourseAndEventDatePage,
  PublicTimetableWidget
} from '@/service/__generated-api'
import type { Widget } from '@/types/utils'

const props = defineProps<Widget<PublicTimetableWidget>>()

const pageSize = computed(() => (props.isFirst ? 21 : 6))
const filtersOverride = computed(() => {
  if (!props.widget.filter_type || !props.widget.filter_value) return {}
  return { [props.widget.filter_type]: props.widget.filter_value }
})
const applyNonSelectableFilters = computed(() => props.isFirst)

const {
  query,
  filters,
  setFilter,
  resetFilters,
  page,
  hasNextPage,
  changePage,
  setHasNextPage
} = useTimetableQuery({
  widgetId: props.widget.id,
  pageSize,
  applyNonSelectableFilters,
  filtersOverride
})

const { data: timetableData } = await useAsyncData(
  `${props.widget.id}-timetable-data`,
  async app => {
    const data = await useApi().publicPagesCourseAndEventsDatesList(query.value)
    const { next: nextPage, results: currentResult } = data
    const previousResult: PublicCourseAndEventDatePage[] | undefined =
      app?._asyncData?.[`${props.widget.id}-timetable-data`]?.data.value
        ?.results

    setHasNextPage(!!nextPage)

    return {
      ...data,
      results:
        page.value > 1 && previousResult
          ? [...previousResult, ...currentResult]
          : currentResult
    }
  },
  {
    // This is due to the fact that the query object
    // is constantly changing as a whole, so we need to do it like this
    // to prevent from constant re-renders and backend calls
    watch: [
      () => query.value?.page,
      () => query.value?.page_size,
      () => query.value?.course,
      () => query.value?.level,
      () => query.value?.location,
      () => query.value?.participant_type,
      () => query.value?.teacher
    ]
  }
)

const display = ref<'grid' | 'list'>('list')
</script>

<style lang="scss" scoped>
.timetable-widget {
  row-gap: rem(32px);

  @include media-up(s-lg) {
    row-gap: rem(52px);
  }
}

.title {
  @include media-column(
    (
      xs: 12,
      s-lg: 6 0
    )
  );
}

.description {
  @include media-column(
    (
      xs: 12,
      s-lg: 6 6
    )
  );
  margin-bottom: rem(20px);
  color: $layout-text-02;

  @include media-up(s-lg) {
    margin-bottom: 0;
  }
}

.wrapper {
  @include column(xs, 12);
}
</style>
